import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "../../services/toast";

import { makeStyles } from 'tss-react/mui';
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ReplayIcon from "@mui/icons-material/Replay";
import Tooltip from "@mui/material/Tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { StandardTableContainer, useStandardTableStyles } from "../../components/StandardTable";
import AppointmentsListPanel from "../../components/AppointmentsListPanel";
import AppointmentsCalendarPanel from "../../components/AppointmentsCalendarPanel";
import AppointmentModal from "../../components/AppointmentModal";
import useAppointmentsData from "../../hooks/useAppointmentsData";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { capitalize } from "lodash";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const getUrlParam = (param) => {
  return new URLSearchParams(window.location.search).get(param);
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1.5),
    overflowY: "scroll",
    background: "transparent",
    ...theme.scrollbarStyles,
  },
}));

const Schedules = () => {
  const { classes } = useStyles();
  const tableClasses = useStandardTableStyles();

  const [tab, setTab] = useState("calendar");
  const { user } = useContext(AuthContext);
  const appointmentsData = useAppointmentsData();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    handleOpenScheduleModalFromContactId();
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedules });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.scheduleId });
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOpenScheduleModalFromContactId, user?.companyId]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleRetrySchedule = async (scheduleId) => {
    try {
      await api.put(`/schedules/${scheduleId}/retry`);
      toast.success(i18n.t("schedules.toasts.retry"));
    } catch (err) {
      toastError(err);
    }
    await fetchSchedules();
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        reload={fetchSchedules}
        aria-labelledby="form-dialog-title"
        scheduleId={selectedSchedule && selectedSchedule.id}
        contactId={contactId}
        cleanContact={cleanContact}
      />
      <MainHeader>
        <Title>{i18n.t("schedules.title")}</Title>
        {tab === "messages" && (
          <MainHeaderButtonsWrapper>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenScheduleModal}
            >
              {i18n.t("schedules.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        )}
        {(tab === "appointments" || tab === "calendar") && (
          <MainHeaderButtonsWrapper>
            {appointmentsData.isAdmin && (
              <TextField
                select
                size="small"
                label="Agente"
                value={appointmentsData.userFilter}
                onChange={(e) => appointmentsData.setUserFilter(e.target.value)}
                style={{ minWidth: 160 }}
              >
                <MenuItem value="">Todos</MenuItem>
                {appointmentsData.users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button variant="contained" color="primary" onClick={appointmentsData.openNew}>
              Nueva cita
            </Button>
          </MainHeaderButtonsWrapper>
        )}
      </MainHeader>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Calendario" value="calendar" />
        <Tab label="Agendamiento de Cita" value="appointments" />
        <Tab label="Agendamiento de Mensajes" value="messages" />
      </Tabs>
      <ConfirmationModal
        title="Eliminar cita"
        open={appointmentsData.confirmOpen}
        onClose={appointmentsData.setConfirmOpen}
        onConfirm={appointmentsData.confirmDelete}
      >
        ¿Realmente deseas eliminar esta cita?
      </ConfirmationModal>
      <AppointmentModal
        open={appointmentsData.modalOpen}
        appointment={appointmentsData.selectedAppointment}
        initialStart={appointmentsData.initialStart}
        users={appointmentsData.users}
        currentUser={appointmentsData.currentUser}
        canAssignOthers={appointmentsData.isAdmin}
        onClose={() => appointmentsData.setModalOpen(false)}
        onSaved={appointmentsData.loadAppointments}
      />
      {tab === "messages" ? (
        <div className={classes.mainPaper} onScroll={handleScroll}>
          <StandardTableContainer>
            <Table size="small" stickyHeader>
              <TableHead className={tableClasses.head}>
                <TableRow>
                  <TableCell>{i18n.t("schedules.table.contact")}</TableCell>
                  <TableCell>{i18n.t("schedules.table.body")}</TableCell>
                  <TableCell>{i18n.t("schedules.table.sendAt")}</TableCell>
                  <TableCell align="center">{i18n.t("schedules.table.status")}</TableCell>
                  <TableCell align="center">{i18n.t("schedules.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id} hover>
                    <TableCell>{schedule.contact.name}</TableCell>
                    <TableCell title={schedule.body}>
                      {truncate(schedule.body, 25)}
                    </TableCell>
                    <TableCell>
                      {moment(schedule.sendAt).format("DD/MM/YYYY HH:mm:ss")}
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" variant="outlined" label={capitalize(schedule.status)} />
                    </TableCell>
                    <TableCell align="center">
                      {schedule.status === "ERRO" && (
                        <Tooltip title={i18n.t("schedules.buttons.retry")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleRetrySchedule(schedule.id)}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEditSchedule(schedule)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingSchedule(schedule);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
            {!loading && schedules.length === 0 && (
              <div className={tableClasses.empty}>Todavía no hay ningún agendamiento cargado.</div>
            )}
          </StandardTableContainer>
        </div>
      ) : tab === "appointments" ? (
        <div className={classes.mainPaper}>
          <AppointmentsListPanel
            appointments={appointmentsData.appointments}
            loading={appointmentsData.loading}
            onEdit={appointmentsData.openEdit}
            onDelete={appointmentsData.requestDelete}
          />
        </div>
      ) : (
        <div className={classes.mainPaper}>
          <AppointmentsCalendarPanel
            appointments={appointmentsData.appointments}
            onNewAt={appointmentsData.openNewAt}
            onEdit={appointmentsData.openEdit}
          />
        </div>
      )}
    </MainContainer>
  );
};

export default Schedules;
